import {
  ApiErrorText,
  Button,
  ErrorText,
  Loader,
  ModalComponentWrapper,
} from '@/shared/components';
import { useTranslation } from 'react-i18next';
import { useApiTokensModalOptions, useApiTokensModal } from '../model';
import { useGetAccountApiTokens, type ApiToken } from '@/entities/api-token';
import { AccountSelectInput } from './AccountSelectInput';
import { ApiTokensContent } from './ApiTokensContent';
import { useCallback, useState } from 'react';
import { useConfirmationModal } from '@/widgets';
import { useDeleteApiTokenFlow } from '@/features/delete-api-token';
import { useCreateApiTokenFlow } from '@/features/create-api-token';
import { useEditApiTokenModal } from '@/features/edit-api-token';

type Props = {
  accountNumber: string;
};

export function ApiTokensListModal({ accountNumber }: Props) {
  const { t } = useTranslation();
  const { openModal, Modal: ConfirmationModal } = useConfirmationModal();

  const [selectedAccountNumber, setSelectedAccountNumber] =
    useState<string>(accountNumber);

  const {
    data: apiTokens,
    isFetching: isApiTokensLoading,
    error: apiTokensError,
    refetch: refetchApiTokens,
  } = useGetAccountApiTokens(selectedAccountNumber);

  const { openApiTokensModal } = useApiTokensModal();
  const {
    accounts,
    accessTypesMap,
    isLoading: isApiTokenFormDataLoading,
    error: apiTokenFormDataError,
  } = useApiTokensModalOptions();

  const { createApiToken, isCreateApiTokenPending, createApiTokenError } =
    useCreateApiTokenFlow({
      accountNumber: selectedAccountNumber,
      onBack: () => openApiTokensModal(selectedAccountNumber),
    });
  const { openEditApiTokenModal } = useEditApiTokenModal();
  const { deleteApiToken } = useDeleteApiTokenFlow({
    accountNumber: selectedAccountNumber,
    onDeletionSuccess: refetchApiTokens,
    openConfirmationModal: openModal,
    onBack: () => openApiTokensModal(selectedAccountNumber),
  });

  const canRenderContent =
    !isApiTokenFormDataLoading &&
    accounts &&
    accessTypesMap &&
    !apiTokenFormDataError;

  const handleCreateApiToken = useCallback(
    () => selectedAccountNumber && createApiToken(),
    [selectedAccountNumber, createApiToken]
  );

  const handleEditApiToken = useCallback(
    (apiToken: ApiToken) => {
      if (!selectedAccountNumber) return;
      openEditApiTokenModal({
        apiToken,
        accountNumber: selectedAccountNumber,
        onBack: () => openApiTokensModal(selectedAccountNumber),
      });
    },
    [selectedAccountNumber, openEditApiTokenModal, openApiTokensModal]
  );

  const handleDeleteApiToken = useCallback(
    (id: string) => selectedAccountNumber && deleteApiToken(id),
    [selectedAccountNumber, deleteApiToken]
  );

  return (
    <ModalComponentWrapper
      title={t('webApiTokens.title')}
      contentClassName="scrollbar-hide"
      headerClassName="border-transparent"
      footer={
        canRenderContent ? (
          <div className="flex flex-col gap-[0.8rem]">
            {createApiTokenError && (
              <ErrorText size="small">{createApiTokenError}</ErrorText>
            )}

            <Button
              size="lg"
              variant={apiTokens?.webtokens?.length ? 'secondary' : 'default'}
              disabled={isApiTokensLoading}
              loading={isCreateApiTokenPending}
              onClick={handleCreateApiToken}>
              {t(
                apiTokens?.webtokens?.length
                  ? 'webApiTokens.creation.buttonText'
                  : 'webApiTokens.emptyState.buttonText'
              )}
            </Button>
          </div>
        ) : null
      }>
      {isApiTokenFormDataLoading && <Loader />}
      {apiTokenFormDataError && <ApiErrorText error={apiTokenFormDataError} />}

      {canRenderContent && (
        <div className="flex flex-col gap-[1.6rem]">
          <div className="sticky top-[calc(var(--header-height)+1.8rem)] z-[2] before:absolute before:inset-x-0 before:-top-[1.8rem] before:-z-10 before:h-[4.8rem] before:bg-background">
            <AccountSelectInput
              selectedAccount={selectedAccountNumber}
              setSelectedAccount={setSelectedAccountNumber}
              accounts={accounts}
            />
          </div>

          <ApiTokensContent
            isApiTokensLoading={isApiTokensLoading}
            apiTokensError={apiTokensError}
            apiTokens={apiTokens?.webtokens || []}
            onEdit={handleEditApiToken}
            onDelete={handleDeleteApiToken}
            accessTypes={accessTypesMap}
          />
        </div>
      )}

      {ConfirmationModal}
    </ModalComponentWrapper>
  );
}
